import './sp-preview-box';

import {css, html, LitElement} from 'lit';
import {customElement} from 'lit/decorators.js';
import {property} from 'lit/decorators.js';
import {when} from 'lit/directives/when.js';
import {SpRemoteFile} from "../inputs/interfaces";
import {getIcon} from "../../styles/icons";

@customElement('sp-preview-boxes')
export class SpPreviewBoxes extends LitElement {
    static styles = [
        css`
        :host {
            height: 2.25rem;
            display: grid;
            justify-content: center;
        }
        
        .preview-boxes-container {
            align-self: center;
            display: grid;
            grid-auto-flow: column;
        }`
    ]
    
    @property({ type: Boolean })
    public displayIcon = true;
    
    @property({ type: String })
    public iconStyle = getIcon('plus', { size: 'x128' });
    
    @property({ type: Number })
    public displayCount = 7;
    
    @property({ type: String })
    public selectedId: string;
    
    @property({ type: Array })
    public previewImages: PreviewBoxContent[];
    
    private iconClickEvent = new CustomEvent(
        'icon-click'
    );
    
    private selectionChangeEvent: PreviewBoxesSelectionChangedEvent = new CustomEvent(
        'selection-change',
        {
            detail: {
                selectedId: null
            },
            composed: true
        }
    );
    
    protected render() {
        const selectedIndex = this.previewImages.findIndex(previewImage => previewImage.id === this.selectedId);
        const bounds = this.getBounds(selectedIndex);
        return html`
            <div class="preview-boxes-container">
                ${ when(this.displayIcon, () => 
                        html`<sp-preview-box 
                                @click=${ event => {
                                    this.dispatchEvent(this.iconClickEvent);
                                } } 
                                .icon=${ this.iconStyle }></sp-preview-box>`) }
                ${ when(this.previewImages, () =>
                    this.previewImages.map((previewImage, index) => {
                        if (selectedIndex > -1 && selectedIndex === index) {
                            return html`<sp-preview-box .selected=${ true } .previewImage=${ this.previewImages[index] }></sp-preview-box>`;
                        } else if ((index < selectedIndex && index >= selectedIndex - bounds.takeLeft) ||
                            (index > selectedIndex && index <= selectedIndex + bounds.takeRight)) {
                            return html`<sp-preview-box  @click=${
                                event => this.previewClicked(previewImage.id, event) } .previewImage=${ this.previewImages[index] }></sp-preview-box>`;
                        }
                    })) }
            </div> `;
    }
    
    protected previewClicked(id: string, event: MouseEvent) {
        this.selectionChangeEvent.detail.selectedId = +id;
        this.dispatchEvent(this.selectionChangeEvent);
    }
    
    protected getBounds(selectedIndex: number) {
        const middle = Math.round(this.displayCount / 2);
        const leftMax = middle - 1;
        const rightMax = this.displayCount - middle;
        const leftAvailable = selectedIndex;
        const rightAvailable = this.previewImages.length - (selectedIndex + 1);
        if (leftAvailable < leftMax && rightAvailable < rightMax) {
            return { takeLeft: leftAvailable, takeRight: rightAvailable };
        } else if (leftAvailable < leftMax && rightAvailable >= rightMax) {
            const takeRight = rightMax + (leftMax - leftAvailable) > rightAvailable ? rightAvailable : rightMax + (leftMax - leftAvailable);
            return { takeLeft: leftAvailable, takeRight };
        } else if (leftAvailable >= leftMax && rightAvailable < rightMax) {
            const takeLeft = leftMax + (rightMax - rightAvailable) > leftAvailable ? leftAvailable : leftMax + (rightMax - rightAvailable);
            return { takeLeft, takeRight: rightAvailable };
        } else {
            return { takeLeft: leftMax, takeRight: rightMax };
        }
    }
}

export interface PreviewBoxContent {
    id: string;
    image?: SpRemoteFile;
}

export interface PreviewBoxesSelectionChangedEvent extends Event {
    detail: {
        selectedId: number;
    };
}
